import { Box } from '@hover/blueprint';
import type { PropsWithChildren } from 'react';
import React, { createContext, useContext, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { animated, useTransition } from 'react-spring';
import { v4 as uuidV4 } from 'uuid';

import { TOOLBAR_HEIGHT } from 'src/modules/designStudio/components/Toolbar';
import { Mode, ModeContext } from 'src/modules/designStudio/providers';
import { Toast } from 'src/shared/components';
import type { SliceOfToast } from 'src/shared/components/Toast/types';
import { useIsLandscape } from 'src/shared/hooks/useIsLandscape';
import { isServer } from 'src/shared/utils/environment';

const TOAST_PORTAL_ELEMENT_ID = 'toast-portal';

interface ToastContext {
  toasts: SliceOfToast[];
  addToast: (bread: Bread) => void;
  removeToast: (toast: SliceOfToast, currentToasts: SliceOfToast[]) => void;
}

const ToastPortal = () => {
  const { mode } = useContext(ModeContext);
  if (mode === Mode.DEFAULT) {
    return <div id={TOAST_PORTAL_ELEMENT_ID} />;
  }
  return null;
};

const context = createContext<ToastContext>({
  toasts: [],
  addToast: () => undefined,
  removeToast: () => undefined,
});

type Props = PropsWithChildren<{}>;

type Bread = Omit<SliceOfToast, 'id'>;

const useToaster = () => useContext(context);

const useMountToast = (containerId: string) => {
  useEffect(() => {
    if (!isServer()) {
      const toast = document.getElementById(TOAST_PORTAL_ELEMENT_ID);
      if (toast) document.querySelector(`#${containerId}`)?.prepend(toast);

      return () => {
        if (toast) document.querySelector('#toast-provider')?.prepend(toast);
      };
    }

    return () => undefined;
  }, [containerId]);
};

const Provider = ({ children, ...props }: Props) => {
  const [toasts, setToasts] = useState<SliceOfToast[]>([]);
  const [isLandscape] = useIsLandscape();
  // utilizes a Portal due to z-index issues when elements are
  // in different stacking context
  const portalEl = isServer()
    ? null
    : document.getElementById(TOAST_PORTAL_ELEMENT_ID);

  // can potentially make all these positions configurable
  // to support direction and placement of toast dynamically
  const transitions = useTransition(toasts, {
    keys: item => item.id,
    from: item => {
      const yOffset = item.offset ?? (isLandscape ? '0' : '-135%');

      return {
        transform: `translate3d(0, ${yOffset},0)`,
        opacity: 0,
        position: 'absolute',
        top: isLandscape ? '32px' : '-10px',
        width: '100%',
      };
    },
    enter: item => {
      const yOffset = item.offset ?? (isLandscape ? '0' : '-135%');
      return { transform: `translate3d(0,${yOffset},0)`, opacity: 1 };
    },
    leave: item => {
      const yOffset = item.offset ?? (isLandscape ? '0' : '-135%');
      return {
        transform: `translate3d(0, ${
          item.type === 'active' ? TOOLBAR_HEIGHT : yOffset
        },0)`,
        opacity: 0,
      };
    },
    config: {
      duration: 300,
    },
  });

  const addToast = (bread: Bread) => {
    const isSameToastBeingAdded =
      bread.title === toasts[0]?.title &&
      bread.subtitle === toasts[0]?.subtitle;

    if (isSameToastBeingAdded) {
      return;
    }
    const id: string = uuidV4();
    const toast = { ...bread, id } as SliceOfToast;

    const newToasts = [toast];
    setToasts(newToasts);
  };

  const removeToast = (toast: SliceOfToast, currentToasts: SliceOfToast[]) => {
    const newToasts = currentToasts.filter(t => t !== toast);
    setToasts(newToasts);
  };

  return (
    // See DS-846
    // eslint-disable-next-line react/jsx-no-constructed-context-values
    <context.Provider value={{ toasts, addToast, removeToast }} {...props}>
      <Box id="toast-provider">
        <ToastPortal />
        {children}
        {portalEl &&
          ReactDOM.createPortal(
            transitions((style, toast) => (
              <animated.div style={style}>
                <Toast toast={toast} toasts={toasts} />
              </animated.div>
            )),
            portalEl,
          )}
      </Box>
    </context.Provider>
  );
};

export {
  Provider,
  useToaster,
  Bread,
  ToastPortal,
  TOAST_PORTAL_ELEMENT_ID,
  useMountToast,
};
